import React from 'react';
import { Target, Users, Lightbulb, ShieldCheck } from 'lucide-react';
import { Value, Stat } from '../types';
import '../styles/About.css';

const About: React.FC = () => {
  const values: Value[] = [
    { id: 1, icon: Target, title: 'Outcome-first', description: 'We write success criteria before we write code. If the agent doesn\'t move a number, we didn\'t ship.' },
    { id: 2, icon: Users, title: 'Partners, not vendors', description: 'You get a small senior team on Slack, not a pod of juniors reading from a backlog.' },
    { id: 3, icon: Lightbulb, title: 'Pragmatic AI', description: 'We use frontier models where they help, classical software where it\'s cheaper, and people where they belong.' },
    { id: 4, icon: ShieldCheck, title: 'Production, not demos', description: 'Observability, rollback, and audit trails from day one. Nothing ships without a kill-switch.' },
  ];

  const stats: Stat[] = [
    { id: 1, number: '50+', label: 'Deployments shipped' },
    { id: 2, number: '98%', label: 'Client retention' },
    { id: 3, number: '3×', label: 'Average first-year ROI' },
    { id: 4, number: '<30d', label: 'From kickoff to live' },
  ];

  return (
    <section id="about" className="about section-padding">
      <div className="container">
        <div className="about-layout">
          <div className="about-content reveal">
            <span className="eyebrow">About Datryx</span>
            <h2>Built by operators who got tired of demos that didn&rsquo;t ship.</h2>
            <p>
              Datryx is a small, senior team of engineers, ML practitioners, and operators.
              We spent years inside ops, trading desks, and product teams before starting
              Datryx—so we know the difference between a working agent and a good slide.
            </p>
            <p>
              We work as embedded automation partners. You get a single Slack channel, a
              weekly demo, code you own, and an agent that earns its keep from week one.
            </p>

            <div className="values-section">
              <h3>How we operate</h3>
              <div className="values-grid">
                {values.map((value: Value) => {
                  const IconComponent = value.icon;
                  return (
                    <div key={value.id} className="value-card">
                      <div className="value-icon">
                        <IconComponent size={16} />
                      </div>
                      <div>
                        <h4>{value.title}</h4>
                        <p>{value.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <aside className="stats-card reveal">
            <div className="stats-card-inner">
              <div className="stats-heading">
                <span className="eyebrow">By the numbers</span>
                <h3>Boring metrics. Loud impact.</h3>
              </div>
              <div className="stats-grid">
                {stats.map((stat: Stat) => (
                  <div key={stat.id} className="stat-item">
                    <div className="stat-number gradient-text">{stat.number}</div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>
              <div className="stats-footnote">
                Most engagements pay back inside the first quarter. We scope before we sell.
              </div>
            </div>
            <div className="stats-glow" aria-hidden="true" />
          </aside>
        </div>
      </div>
    </section>
  );
};

export default About;
